/* eslint-disable @typescript-eslint/no-explicit-any */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { Route, Router, Switch } from 'react-router-dom';

import AppFlightNav from 'components/AppFlightNav/AppFlightNav';
import GoogleAnalyticsTracker from 'components/GoogleAnalyticsTracker/GoogleAnalyticsTracker';
import Pages from 'pages';
import PrioritizationProvider from 'pages/Zones/PrioritizationContext/PrioritizationProvider';
import PrivateRoute from 'components/PrivateRoute/PrivateRoute';
import SnackBar from 'components/SnackBar/SnackBar';
import { history } from 'store/store';
import { isAxiosError } from 'axios';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { useSelector } from 'react-redux';

// TODO: fix imported components' CSS; but in the meantime we need this at the bottom to prevent some very boosted overrides
import './App.scss';

function App() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { flags } = useFeatureFlag();
  const user = useSelector((state: any) => state.auth.user);
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30 * 1000, // 30 seconds,
            cacheTime: 5 * 60 * 1000, // 5 minutes
            networkMode: 'always',
            retry: (retryCount, error) => {
              // Do not retry if it's HTTP 500 series
              if (
                error instanceof Error &&
                isAxiosError(error) &&
                error.response?.status &&
                error.response.status >= 500 &&
                error.response.status < 600
              ) {
                return false;
              }

              // Maximum number of retries for other errors
              if (retryCount >= 3) {
                return false;
              }

              return true;
            },
          },
        },
      }),
    [],
  );

  return (
    <Router history={history}>
      <QueryClientProvider client={queryClient}>
        <>
          <GoogleAnalyticsTracker />
          <div className="app">
            <Switch>
              <Route exact path="/" component={Pages.NoAccess} />
              <Route exact path="/signed-out/:pid" component={Pages.SignedOut} />
              {/* /redirect route is the initial entry point to this application from other external websites */}
              <Route exact path="/redirect" component={Pages.RedirectIndex} />
              <Route exact path="/auth-callback/:pid" component={Pages.AuthCallback} />
              <Route exact path="/auth-redirect/:pid" component={Pages.AuthRedirect} />
              <Route path="/project/:pid">
                <AppFlightNav user={user} flags={flags}>
                  <div className="app__main">
                    {/* TODO: Use ErrorBoundary Component to render fallback component on error
                      link: https://docs.sentry.io/platforms/javascript/guides/react/components/errorboundary/
                  */}
                    <Switch>
                      <Route exact path="/project/:pid/developer-hub" component={Pages.DeveloperHubIndex} />
                      <Route exact path="/project/:pid/no-access" component={Pages.NoAccess} />

                      <PrivateRoute exact path="/project/:pid" component={Pages.TemplatedExperienceIndex} />
                      <PrivateRoute key="analytics" exact path="/project/:pid/analytics" component={Pages.Analytics} />
                      <PrivateRoute
                        key="analytics-edit-moduleconfig"
                        exact
                        path="/project/:pid/analytics/moduleconfig/:id"
                        component={Pages.CreateOrEditChart}
                      />
                      <PrivateRoute
                        key="analytics-create-moduleconfig"
                        exact
                        path="/project/:pid/analytics/moduleconfig"
                        component={Pages.CreateOrEditChart}
                      />
                      <PrivateRoute
                        exact
                        path="/project/:pid/experiences/templates/library"
                        component={Pages.TemplatesLibrary}
                      />
                      <PrivateRoute
                        exact
                        path="/project/:pid/experiences/templates/library/:cid"
                        component={Pages.TemplatesLibrary}
                      />
                      <PrivateRoute
                        exact
                        path="/project/:pid/experiences/templates/library/:cid/:sid"
                        component={Pages.TemplatesLibrary}
                      />
                      <PrivateRoute
                        exact
                        path="/project/:pid/theme"
                        component={Pages.ThemeHub}
                        requiredFlag="tx_theme_settings"
                      />
                      {/* Experience Canvas Overview */}
                      <PrivateRoute
                        key="experience-overview"
                        exact
                        path="/project/:pid/experiences/:id/overview"
                        component={Pages.ExperienceOverview}
                      />
                      {/* Maker Checker */}
                      <PrivateRoute
                        key="experience-approval-request"
                        exact
                        path="/project/:pid/experiences/:id/approval"
                        component={Pages.ApprovalRequest}
                        requiredFlag="tx_maker_checker"
                      />
                      {/* Touchpoint Editor */}
                      <PrivateRoute
                        key="experience-create-edit"
                        exact
                        path="/project/:pid/experiences/:id/touchpoint/:stepidx"
                        component={Pages.TouchpointEditor}
                      />
                      {/* Audit History */}
                      <PrivateRoute
                        key="audit-history"
                        exact
                        path="/project/:pid/settings/audit-history"
                        component={Pages.AuditHistory}
                      />
                      {/* Location Management */}
                      <PrivateRoute
                        key="location-management"
                        exact
                        path="/project/:pid/location-management"
                        component={Pages.LocationManagement}
                      />
                      {/* Dynamic Content Manager */}
                      <PrivateRoute
                        key="content-manager"
                        exact
                        path="/project/:pid/content-manager"
                        component={Pages.DynamicContentManager}
                      />
                      {/* Zones */}
                      <Route path="/project/:pid/zones/">
                        <PrioritizationProvider>
                          <Switch>
                            <PrivateRoute
                              key="zones-onboarding"
                              exact
                              path="/project/:pid/zones/onboarding"
                              component={Pages.ZonesOnboarding}
                            />
                            <PrivateRoute key="zone" exact path="/project/:pid/zones/:zoneId" component={Pages.Zone} />
                            <PrivateRoute key="zones" exact path="/project/:pid/zones" component={Pages.Zones} />
                          </Switch>
                        </PrioritizationProvider>
                      </Route>
                      {/* Flow Builder */}
                      <PrivateRoute
                        key="flow-builder"
                        exact
                        path="/project/:pid/experiences/:id/flow-builder"
                        component={Pages.FlowBuilder}
                      />
                      <PrivateRoute
                        key="flow-builder-v2"
                        exact
                        path="/project/:pid/experiences/:id/flow-builder-v2"
                        component={Pages.FlowBuilderV2}
                        requiredFlag="tx_flow_v2"
                      />
                      {/* Merchant Portal*/}
                      <Route path="/project/:pid/merchants" component={Pages.RedirectIndex}>
                        <PrivateRoute
                          key="merchant-portal"
                          exact
                          path="/project/:pid/merchants"
                          component={Pages.MerchantPortal}
                          requiredFlag="tx_merchant_portal"
                        />
                        <PrivateRoute
                          key="merchant-portal-edit-merchant"
                          exact
                          path="/project/:pid/merchants/:merchantid/edit-merchant"
                          component={Pages.EditMerchant}
                          requiredFlag="tx_merchant_portal"
                        />
                        <PrivateRoute
                          key="merchant-portal-create-offer"
                          exact
                          path="/project/:pid/merchants/:merchantid/create-offer"
                          component={Pages.CreateMerchantOffer}
                          requiredFlag="tx_merchant_portal"
                        />
                        <PrivateRoute
                          key="merchant-portal-edit-offer"
                          exact
                          path="/project/:pid/merchants/:merchantid/offers/:offerid/edit-offer"
                          component={Pages.EditMerchantOffer}
                          requiredFlag="tx_merchant_portal"
                        />
                      </Route>
                      <PrivateRoute
                        key="merchant-portal-register-merchant"
                        exact
                        path="/project/:pid/register-merchant"
                        component={Pages.RegisterMerchant}
                      />
                      <Route path="*" component={Pages.NotFoundIndex} />
                    </Switch>
                  </div>
                </AppFlightNav>
              </Route>
              <Route path="*" component={Pages.NotFoundIndex} />
            </Switch>
            <SnackBar />
          </div>
        </>
      </QueryClientProvider>
    </Router>
  );
}

export default App;
